import { Box, Flex, Text } from '@chakra-ui/react';
import { useDailyRoom } from '@web-app/components/CallV2/hooks/useDailyRoom';
import GoalsList from '@web-app/components/GoalsList';
import { GoalsAndInterventionsNotesProps } from '@web-app/components/GoalsList/types';
import { useSaveSessionNote } from '@web-app/hooks/mutations/session-notes/saveSessionNote';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useSessionNote } from '@web-app/hooks/useSessionNote';
import { Note } from '@web-app/server/api/types';
import { getNotesDataV3 } from '@web-app/utils/api/provider-notes';
import { getSessionNoteData } from '@web-app/utils/api/session-notes/utils';
import { useMemo } from 'react';

interface GoalsV2Props {
  appointment?: AppointmentResponse;
}

/**
 * V2 version of goals component for CallV2 layout.
 * Positioned below the PatientBenefitsV2 in the scrollable right panel.
 * Manages goals and interventions for the current session.
 */
export const GoalsV2 = ({ appointment }: GoalsV2Props) => {
  const { roomId } = useDailyRoom();
  const { sessionNote, setSessionNote } = useSessionNote();
  const { mutate: saveProviderNotes } = useSaveSessionNote();

  const mappedNotes = useMemo(() => {
    return getNotesDataV3(sessionNote);
  }, [sessionNote]);

  const updateNotes = (data: Partial<GoalsAndInterventionsNotesProps>) => {
    const updatedNotes = getSessionNoteData({
      data,
      sessionNote,
      roomId,
      appointment,
    });
    setSessionNote((prev) =>
      prev ? { ...prev, ...updatedNotes } : (updatedNotes as Note),
    );
    saveProviderNotes(updatedNotes);
  };

  return (
    <Box background="green.100" borderRadius={16} padding={4} width="100%">
      <Text fontSize="xs" color="text.muted" mb={3}>
        Clinical goals are for provider use only and are not shared with the
        patient.
      </Text>
      <Flex
        direction="column"
        pb={2}
        h="100%"
        overflowY="auto"
        data-testid="goals-v2"
      >
        {sessionNote && (
          <GoalsList notes={mappedNotes} setNotes={updateNotes} />
        )}
      </Flex>
    </Box>
  );
};
